'use client';

import { CheckCircle, TrendingUp, Shield, Zap, Globe, Award } from 'lucide-react';
import Link from 'next/link';

interface CompellingElementsSectionProps {
  currentLanguage: string;
}

const CompellingElementsSection = ({ currentLanguage }: CompellingElementsSectionProps) => {
  const content = {
    en: {
      title: "Why Choose GenCarbon for Your ESG Journey",
      subtitle: "Industry-Leading Capabilities That Drive Results",
      elements: [
        {
          icon: CheckCircle,
          title: "Regulatory Compliance Made Simple",
          description: "BRSR for Top 500 companies + BRSR Lite for value chain partners addresses the massive compliance pain point",
          color: "from-emerald-500 to-teal-600"
        },
        {
          icon: Zap,
          title: "AI-Driven Precision",
          description: "Spend-to-activity transition with AI/IoT gives real data accuracy that competitors lack",
          color: "from-blue-500 to-cyan-600"
        },
        {
          icon: TrendingUp,
          title: "End-to-End Solution",
          description: "From emission calculation to MRV to carbon credit generation - one-stop shop for sustainability",
          color: "from-purple-500 to-pink-600"
        },
        {
          icon: Globe,
          title: "Multi-Regulatory Expertise",
          description: "BRSR (India), CSRD (Europe), FIBAC (Banking) shows global sophistication and compliance mastery",
          color: "from-orange-500 to-red-600"
        },
        {
          icon: Shield,
          title: "Proven Team Credentials",
          description: "Dr. Nupur's ESG board experience + Mrs. Padmaja's defense/GIS background = unmatched credibility",
          color: "from-indigo-500 to-purple-600"
        },
        {
          icon: Award,
          title: "Quantum-Enhanced Accuracy",
          description: "Advanced algorithms from AI to Quantum computing ensure precise measurement, review, and validation",
          color: "from-green-500 to-emerald-600"
        }
      ],
      cta: "Explore Billion Dollar Opportunities",
      ctaLink: "/billion-dollar-secret-sauce-opportunities"
    },
    hi: {
      title: "अपनी ESG यात्रा के लिए GenCarbon क्यों चुनें",
      subtitle: "उद्योग-अग्रणी क्षमताएं जो परिणाम देती हैं",
      elements: [
        {
          icon: CheckCircle,
          title: "नियामक अनुपालन सरल बनाया",
          description: "शीर्ष 500 कंपनियों के लिए BRSR + मूल्य श्रृंखला भागीदारों के लिए BRSR Lite",
          color: "from-emerald-500 to-teal-600"
        },
        {
          icon: Zap,
          title: "AI-संचालित सटीकता",
          description: "AI/IoT के साथ खर्च-से-गतिविधि संक्रमण वास्तविक डेटा सटीकता प्रदान करता है",
          color: "from-blue-500 to-cyan-600"
        },
        {
          icon: TrendingUp,
          title: "एंड-टू-एंड समाधान",
          description: "उत्सर्जन गणना से लेकर MRV तक कार्बन क्रेडिट उत्पादन - एक-स्टॉप शॉप",
          color: "from-purple-500 to-pink-600"
        },
        {
          icon: Globe,
          title: "बहु-नियामक विशेषज्ञता",
          description: "BRSR (भारत), CSRD (यूरोप), FIBAC (बैंकिंग) वैश्विक परिष्कार दिखाता है",
          color: "from-orange-500 to-red-600"
        },
        {
          icon: Shield,
          title: "सिद्ध टीम प्रमाण-पत्र",
          description: "Dr. Nupur का ESG बोर्ड अनुभव + Mrs. Padmaja की रक्षा/GIS पृष्ठभूमि",
          color: "from-indigo-500 to-purple-600"
        },
        {
          icon: Award,
          title: "क्वांटम-संवर्धित सटीकता",
          description: "AI से लेकर क्वांटम कंप्यूटिंग तक उन्नत एल्गोरिदम सटीक माप सुनिश्चित करते हैं",
          color: "from-green-500 to-emerald-600"
        }
      ],
      cta: "अरब डॉलर के अवसरों का अन्वेषण करें",
      ctaLink: "/billion-dollar-secret-sauce-opportunities"
    },
    ar: {
      title: "لماذا تختار GenCarbon لرحلة ESG الخاصة بك",
      subtitle: "قدرات رائدة في الصناعة تحقق النتائج",
      elements: [
        {
          icon: CheckCircle,
          title: "الامتثال التنظيمي مبسط",
          description: "BRSR لأفضل 500 شركة + BRSR Lite لشركاء سلسلة القيمة",
          color: "from-emerald-500 to-teal-600"
        },
        {
          icon: Zap,
          title: "دقة مدفوعة بالذكاء الاصطناعي",
          description: "الانتقال من الإنفاق إلى النشاط باستخدام AI/IoT يوفر دقة بيانات حقيقية",
          color: "from-blue-500 to-cyan-600"
        },
        {
          icon: TrendingUp,
          title: "حل شامل",
          description: "من حساب الانبعاثات إلى MRV إلى توليد رصيد الكربون - متجر شامل",
          color: "from-purple-500 to-pink-600"
        },
        {
          icon: Globe,
          title: "خبرة تنظيمية متعددة",
          description: "BRSR (الهند)، CSRD (أوروبا)، FIBAC (البنوك) يظهر التطور العالمي",
          color: "from-orange-500 to-red-600"
        },
        {
          icon: Shield,
          title: "بيانات اعتماد الفريق المثبتة",
          description: "خبرة Dr. Nupur في مجلس ESG + خلفية Mrs. Padmaja في الدفاع/GIS",
          color: "from-indigo-500 to-purple-600"
        },
        {
          icon: Award,
          title: "دقة محسّنة بالكم",
          description: "خوارزميات متقدمة من AI إلى الحوسبة الكمومية تضمن القياس الدقيق",
          color: "from-green-500 to-emerald-600"
        }
      ],
      cta: "استكشف فرص المليار دولار",
      ctaLink: "/billion-dollar-secret-sauce-opportunities"
    },
    te: {
      title: "మీ ESG ప్రయాణం కోసం GenCarbon ను ఎందుకు ఎంచుకోవాలి",
      subtitle: "పరిశ్రమ-ముందున్న సామర్థ్యాలు ఫలితాలను అందిస్తాయి",
      elements: [
        {
          icon: CheckCircle,
          title: "నియంత్రణ అనుపాలన సులభం",
          description: "టాప్ 500 కంపెనీలకు BRSR + విలువ గొలుసు భాగస్వాములకు BRSR Lite",
          color: "from-emerald-500 to-teal-600"
        },
        {
          icon: Zap,
          title: "AI-నడిచే ఖచ్చితత్వం",
          description: "AI/IoT తో ఖర్చు-నుండి-కార్యాచరణ పరివర్తన నిజమైన డేటా ఖచ్చితత్వం ఇస్తుంది",
          color: "from-blue-500 to-cyan-600"
        },
        {
          icon: TrendingUp,
          title: "ఎండ్-టు-ఎండ్ పరిష్కారం",
          description: "ఉద్గార గణన నుండి MRV వరకు కార్బన్ క్రెడిట్ ఉత్పత్తి వరకు - వన్-స్టాప్ షాప్",
          color: "from-purple-500 to-pink-600"
        },
        {
          icon: Globe,
          title: "బహు-నియంత్రణ నైపుణ్యం",
          description: "BRSR (భారతదేశం), CSRD (యూరప్), FIBAC (బ్యాంకింగ్) ప్రపంచ అధునాతనతను చూపిస్తుంది",
          color: "from-orange-500 to-red-600"
        },
        {
          icon: Shield,
          title: "నిరూపితమైన టీమ్ ఆధారాలు",
          description: "Dr. Nupur యొక్క ESG బోర్డు అనుభవం + Mrs. Padmaja యొక్క రక్షణ/GIS నేపథ్యం",
          color: "from-indigo-500 to-purple-600"
        },
        {
          icon: Award,
          title: "క్వాంటమ్-మెరుగైన ఖచ్చితత్వం",
          description: "AI నుండి క్వాంటమ్ కంప్యూటింగ్ వరకు అధునాతన అల్గారిథమ్‌లు ఖచ్చితమైన కొలతను నిర్ధారిస్తాయి",
          color: "from-green-500 to-emerald-600"
        }
      ],
      cta: "బిలియన్ డాలర్ అవకాశాలను అన్వేషించండి",
      ctaLink: "/billion-dollar-secret-sauce-opportunities"
    }
  };

  const lang = content[currentLanguage as keyof typeof content] || content.en;

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            {lang.title}
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            {lang.subtitle}
          </p>
        </div>

        {/* Elements Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {lang.elements.map((element, index) => {
            const IconComponent = element.icon;
            return (
              <div
                key={index}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                {/* Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${element.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-300`} />
                
                {/* Icon */}
                <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${element.color} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <IconComponent className="w-8 h-8 text-white" />
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-emerald-600 group-hover:to-teal-600 transition-all duration-300">
                  {element.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {element.description}
                </p>

                {/* Decorative Element */}
                <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r ${element.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-b-2xl`} />
              </div>
            );
          })}
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <Link
            href={lang.ctaLink}
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-lg font-semibold rounded-full hover:from-emerald-700 hover:to-teal-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            {lang.cta}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CompellingElementsSection;